import { supabase } from "@/integrations/supabase/client";

/**
 * Utility to populate missing cached covers for books
 * This processes books that have cover_url but no cover_cache_url
 */

interface PopulateResult {
  processed: number;
  succeeded: number;
  failed: number;
  errors: Array<{ bookId: string; error: string }>;
}

/**
 * Process a single batch of books to cache their covers
 */
export async function processCoverCacheBatch(
  books: Array<{ id: string; isbn13?: string; isbn10?: string; open_library_id?: string; title: string; authors?: string[] }>,
  onProgress?: (current: number, total: number) => void
): Promise<PopulateResult> {
  const result: PopulateResult = {
    processed: 0,
    succeeded: 0,
    failed: 0,
    errors: []
  };

  for (let i = 0; i < books.length; i++) {
    const book = books[i];
    result.processed++;

    try {
      // Call the covers endpoint to cache the cover
      const params = new URLSearchParams();
      params.set('bookId', book.id);
      if (book.isbn13) params.set('isbn13', book.isbn13);
      if (book.isbn10) params.set('isbn10', book.isbn10);
      if (book.open_library_id) params.set('olid', book.open_library_id);
      params.set('size', 'md');
      params.set('cache', 'true'); // Tell endpoint to cache the result

      const coverUrl = `https://zjubnjpiajoscjmogyqb.supabase.co/functions/v1/covers?${params.toString()}`;
      
      // Make HEAD request to trigger caching without downloading image
      const response = await fetch(coverUrl, { method: 'HEAD' });
      
      if (response.ok) {
        result.succeeded++;
        console.log(`[CoverCache] Successfully cached cover for book ${book.id}`);
      } else {
        result.failed++;
        result.errors.push({ 
          bookId: book.id, 
          error: `HTTP ${response.status}` 
        });
      }

      // Rate limiting: don't overwhelm the server
      if (i < books.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (onProgress) {
        onProgress(i + 1, books.length);
      }
    } catch (error) {
      result.failed++;
      result.errors.push({ 
        bookId: book.id, 
        error: error instanceof Error ? error.message : String(error)
      });
      console.error(`[CoverCache] Failed to cache cover for book ${book.id}:`, error);
    }
  }

  return result;
}

/**
 * Find and process all books missing cached covers
 */
export async function populateAllMissingCachedCovers(
  batchSize: number = 50,
  onProgress?: (current: number, total: number, result?: PopulateResult) => void
): Promise<PopulateResult> {
  const totalResult: PopulateResult = {
    processed: 0,
    succeeded: 0,
    failed: 0,
    errors: []
  };

  try {
    // Find books with cover_url but no cover_cache_url
    const { data: books, error } = await supabase
      .from('books')
      .select('id, isbn13, isbn10, open_library_id, title, authors')
      .not('cover_url', 'is', null)
      .is('cover_cache_url', null)
      .limit(1000); // Process in chunks of 1000

    if (error) {
      throw error;
    }

    if (!books || books.length === 0) {
      console.log('[CoverCache] No books found needing cover caching');
      return totalResult;
    }

    console.log(`[CoverCache] Found ${books.length} books needing cover caching`);

    // Process in batches
    for (let i = 0; i < books.length; i += batchSize) {
      const batch = books.slice(i, i + batchSize);
      console.log(`[CoverCache] Processing batch ${Math.floor(i / batchSize) + 1} (${batch.length} books)`);

      const batchResult = await processCoverCacheBatch(batch, (current, total) => {
        if (onProgress) {
          onProgress(i + current, books.length);
        }
      });

      totalResult.processed += batchResult.processed;
      totalResult.succeeded += batchResult.succeeded;
      totalResult.failed += batchResult.failed;
      totalResult.errors.push(...batchResult.errors);

      // Brief pause between batches
      if (i + batchSize < books.length) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    console.log('[CoverCache] Completed:', totalResult);
    return totalResult;
  } catch (error) {
    console.error('[CoverCache] Error in populateAllMissingCachedCovers:', error);
    throw error;
  }
}

/**
 * Get stats on cover caching status
 */
export async function getCoverCacheStats() {
  try {
    const [totalWithCovers, totalCached, totalMissing] = await Promise.all([
      supabase
        .from('books')
        .select('id', { count: 'exact', head: true })
        .not('cover_url', 'is', null),
      supabase
        .from('books')
        .select('id', { count: 'exact', head: true })
        .eq('cover_cached', true),
      supabase
        .from('books')
        .select('id', { count: 'exact', head: true })
        .not('cover_url', 'is', null)
        .is('cover_cache_url', null)
    ]);

    return {
      totalWithCovers: totalWithCovers.count || 0,
      totalCached: totalCached.count || 0,
      totalMissing: totalMissing.count || 0,
      percentCached: totalWithCovers.count 
        ? Math.round((totalCached.count || 0) / totalWithCovers.count * 100) 
        : 0
    };
  } catch (error) {
    console.error('[CoverCache] Error getting stats:', error);
    throw error;
  }
}
